/**
 * Formulários do prontuário: o do documento (cinco itens do 20.6.1) e o do simulado, que é outro
 * desenho — resultado obrigatório, sem próxima revisão e sem arquivo.
 */
import React from 'react';
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  FLAMMABLE_RECORD_METHODOLOGIES,
  FLAMMABLE_RECORD_RESULTS,
  ROTULO_DA_METODOLOGIA,
  ROTULO_DO_RESULTADO,
  type FlammableRecordType,
} from '../../../../../types/flammableInstallation';
import { hojeComoTexto } from '../../flammableDates';
import { DICA_DO_ARQUIVO, EXTENSOES_ACEITAS, tamanhoEmTexto } from './arquivoDoRegistro';
import type { RegistroErrors, RegistroValues } from './registroDoFormulario';

interface FormularioDoRegistroProps {
  titulo: string;
  tipo: FlammableRecordType;
  values: RegistroValues;
  errors: RegistroErrors;
  /** Frase do servidor, ou a que pede para corrigir os campos marcados. */
  erro: string | null;
  salvando: boolean;
  /** Arquivo escolhido para subir junto, e o que a escolha tem de errado. */
  arquivo: File | null;
  erroDoArquivo: string | null;
  /** Nome do arquivo que o registro já tem (edição). */
  arquivoAtual: string | null;
  onChange: (patch: Partial<RegistroValues>) => void;
  /** Quem recebe o arquivo escolhido confere formato e tamanho e devolve a recusa em `erroDoArquivo`. */
  onArquivo: (arquivo: File) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const DATA = { inputLabel: { shrink: true } } as const;

const Moldura: React.FC<{
  titulo: string;
  erro: string | null;
  rotuloDoBotao: string;
  salvando: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}> = ({ titulo, erro, rotuloDoBotao, salvando, onSubmit, onCancel, children }) => (
  <Box
    component="form"
    noValidate
    onSubmit={(event: React.FormEvent) => {
      event.preventDefault();
      onSubmit();
    }}
    sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}
  >
    <Typography variant="subtitle1" component="h5" fontWeight={700} sx={{ mb: 2 }}>
      {titulo}
    </Typography>
    {erro && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {erro}
      </Alert>
    )}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
        gap: 2,
        '& .MuiFormHelperText-root': { fontSize: '1rem' },
      }}
    >
      {children}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
      <Button variant="text" onClick={onCancel} disabled={salvando} sx={{ minHeight: 44 }}>
        Cancelar
      </Button>
      <Button type="submit" variant="outlined" disabled={salvando} sx={{ minHeight: 44 }}>
        {rotuloDoBotao}
      </Button>
    </Box>
  </Box>
);

const SeletorDeArquivo: React.FC<{
  arquivo: File | null;
  arquivoAtual: string | null;
  erro: string | null;
  disabled: boolean;
  onArquivo: (arquivo: File) => void;
}> = ({ arquivo, arquivoAtual, erro, disabled, onArquivo }) => (
  <Box sx={{ gridColumn: { md: '1 / -1' } }}>
    <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={disabled} sx={{ minHeight: 44 }}>
      {arquivo || arquivoAtual ? 'Trocar o arquivo' : 'Escolher arquivo'}
      <input
        hidden
        type="file"
        accept={EXTENSOES_ACEITAS}
        aria-label="Arquivo do documento (PDF, PNG ou JPEG, até 9 MB)"
        onChange={(event) => {
          const escolhido = event.target.files?.[0] ?? null;
          event.target.value = '';
          if (escolhido) onArquivo(escolhido);
        }}
      />
    </Button>
    <Typography
      variant="body2"
      color={erro ? 'error' : 'text.secondary'}
      role={erro ? 'alert' : undefined}
      sx={{ mt: 1, fontSize: '1rem' }}
    >
      {erro ??
        (arquivo
          ? `Arquivo escolhido: ${arquivo.name} (${tamanhoEmTexto(arquivo.size)})`
          : arquivoAtual
            ? `Arquivo atual: ${arquivoAtual}. ${DICA_DO_ARQUIVO}`
            : DICA_DO_ARQUIVO)}
    </Typography>
  </Box>
);

export const FormularioDoDocumento: React.FC<FormularioDoRegistroProps> = ({
  titulo,
  tipo,
  values,
  errors,
  erro,
  salvando,
  arquivo,
  erroDoArquivo,
  arquivoAtual,
  onChange,
  onArquivo,
  onSubmit,
  onCancel,
}) => {
  const analise = tipo === 'ANALISE_RISCOS';
  return (
    <Moldura titulo={titulo} erro={erro} rotuloDoBotao="Salvar documento" salvando={salvando} onSubmit={onSubmit} onCancel={onCancel}>
      <TextField
        required
        type="date"
        label="Data do documento"
        value={values.documentedAt}
        onChange={(event) => onChange({ documentedAt: event.target.value })}
        error={Boolean(errors.documentedAt)}
        helperText={errors.documentedAt ?? 'Quando foi elaborado ou revisado.'}
        disabled={salvando}
        slotProps={{ ...DATA, htmlInput: { max: hojeComoTexto() } }}
        sx={{ gridColumn: { md: 'span 3' } }}
      />
      {analise && (
        <TextField
          select
          required
          label="Metodologia"
          value={values.methodology}
          onChange={(event) => onChange({ methodology: event.target.value as RegistroValues['methodology'] })}
          error={Boolean(errors.methodology)}
          helperText={errors.methodology}
          disabled={salvando}
          sx={{ gridColumn: { md: 'span 3' } }}
        >
          {FLAMMABLE_RECORD_METHODOLOGIES.map((metodologia) => (
            <MenuItem key={metodologia} value={metodologia} sx={{ minHeight: 44 }}>
              {ROTULO_DA_METODOLOGIA[metodologia]}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField
        required
        label="Responsável"
        placeholder="Quem elaborou ou coordenou"
        value={values.responsible}
        onChange={(event) => onChange({ responsible: event.target.value })}
        error={Boolean(errors.responsible)}
        helperText={errors.responsible}
        disabled={salvando}
        sx={{ gridColumn: { md: 'span 3' } }}
      />
      <TextField
        label={analise ? 'Registro profissional' : 'Registro no conselho'}
        placeholder="Ex.: CREA 123456/SP"
        value={values.responsibleRegistration}
        onChange={(event) => onChange({ responsibleRegistration: event.target.value })}
        helperText={
          analise
            ? 'Nas classes II e III a norma pede profissional habilitado: informe o número do registro dele.'
            : undefined
        }
        disabled={salvando}
        sx={{ gridColumn: { md: 'span 3' } }}
      />
      <TextField
        type="date"
        label="Próxima revisão"
        value={values.nextReviewDate}
        onChange={(event) => onChange({ nextReviewDate: event.target.value })}
        error={Boolean(errors.nextReviewDate)}
        helperText={errors.nextReviewDate ?? 'Depois dessa data o documento aparece como "Vencido".'}
        disabled={salvando}
        slotProps={DATA}
        sx={{ gridColumn: { md: 'span 4' } }}
      />
      <TextField
        label="Onde o documento fica guardado"
        placeholder="Ex.: Pasta física na sala da segurança; ou deixe em branco se anexar o arquivo"
        value={values.location}
        onChange={(event) => onChange({ location: event.target.value })}
        disabled={salvando}
        sx={{ gridColumn: { md: 'span 8' } }}
      />
      <TextField
        label="Observação"
        placeholder="Algo que o laudo deva registrar"
        value={values.observations}
        onChange={(event) => onChange({ observations: event.target.value })}
        disabled={salvando}
        sx={{ gridColumn: { md: '1 / -1' } }}
      />
      <SeletorDeArquivo
        arquivo={arquivo}
        arquivoAtual={arquivoAtual}
        erro={erroDoArquivo}
        disabled={salvando}
        onArquivo={onArquivo}
      />
    </Moldura>
  );
};

export const FormularioDoSimulado: React.FC<FormularioDoRegistroProps> = ({
  titulo,
  values,
  errors,
  erro,
  salvando,
  onChange,
  onSubmit,
  onCancel,
}) => (
  <Moldura titulo={titulo} erro={erro} rotuloDoBotao="Salvar simulado" salvando={salvando} onSubmit={onSubmit} onCancel={onCancel}>
    <TextField
      required
      type="date"
      label="Data do simulado"
      value={values.documentedAt}
      onChange={(event) => onChange({ documentedAt: event.target.value })}
      error={Boolean(errors.documentedAt)}
      helperText={errors.documentedAt}
      disabled={salvando}
      slotProps={{ ...DATA, htmlInput: { max: hojeComoTexto() } }}
      sx={{ gridColumn: { md: 'span 3' } }}
    />
    <TextField
      select
      required
      label="Resultado"
      value={values.result}
      onChange={(event) => onChange({ result: event.target.value as RegistroValues['result'] })}
      error={Boolean(errors.result)}
      helperText={errors.result}
      disabled={salvando}
      sx={{ gridColumn: { md: 'span 3' } }}
    >
      {FLAMMABLE_RECORD_RESULTS.map((resultado) => (
        <MenuItem key={resultado} value={resultado} sx={{ minHeight: 44 }}>
          {ROTULO_DO_RESULTADO[resultado]}
        </MenuItem>
      ))}
    </TextField>
    <TextField
      required
      label="Quem conduziu o simulado"
      value={values.responsible}
      onChange={(event) => onChange({ responsible: event.target.value })}
      error={Boolean(errors.responsible)}
      helperText={errors.responsible}
      disabled={salvando}
      sx={{ gridColumn: { md: 'span 6' } }}
    />
    <TextField
      label="Onde o registro fica guardado"
      placeholder="Ex.: Livro de ocorrências da segurança"
      value={values.location}
      onChange={(event) => onChange({ location: event.target.value })}
      disabled={salvando}
      sx={{ gridColumn: { md: 'span 6' } }}
    />
    <TextField
      label="Observação"
      placeholder="Falhas encontradas ou ajustes no plano"
      value={values.observations}
      onChange={(event) => onChange({ observations: event.target.value })}
      disabled={salvando}
      sx={{ gridColumn: { md: 'span 6' } }}
    />
    <Typography variant="body1" color="text.secondary" sx={{ gridColumn: { md: '1 / -1' } }}>
      O simulado não tem data de próxima revisão: o sistema conta 12 meses a partir da data do simulado.
    </Typography>
  </Moldura>
);
